/**
 * Snapshot Formatter Grid Helpers
 *
 * Grid detection, reconciliation, rendering, and grid-specific counting.
 *
 * @module
 */

import type { SnapshotElement } from './snapshot-analyzer.js';
import { isNoisyLabel } from './icon-label-normalizer.js';
import {
  FORM_INPUT_ROLES,
  INTERACTIVE_LEAF_ROLES,
  type GridCell,
  type GridColumn,
  type GridInfo,
  type GridRow,
  getElementText,
  hasInteractiveDescendants,
  isActiveElement,
  isBlankValue,
  isClickableByAttribute,
  stripBlankPatterns,
  subtreeContainsActive,
} from './snapshot-formatter-shared.js';

/**
 * Find the first descendant (or self) with a given role.
 */
function findDescendantByRole(el: SnapshotElement, role: string): SnapshotElement | undefined {
  if (el.role === role) return el;
  for (const child of el.children) {
    const found = findDescendantByRole(child, role);
    if (found) return found;
  }
  return undefined;
}

/**
 * Find the primary label within a column header.
 */
function findPrimaryColumnLabel(columnHeader: SnapshotElement): SnapshotElement | null {
  const search = (el: SnapshotElement): SnapshotElement | null => {
    for (const child of el.children) {
      if (child.role === 'button' && child.text && !child.text.includes('Open menu')) {
        return child;
      }
      if (child.role === 'generic' && getElementText(child) && child.children.length === 0) {
        return child;
      }
      if (child.role === 'generic' && child.children.length > 0) {
        const found = search(child);
        if (found) return found;
      }
    }
    return null;
  };

  return search(columnHeader);
}

/**
 * Detect if a table element is actually an input widget rather than a data table.
 */
export function isWidgetTable(element: SnapshotElement): boolean {
  if (element.role !== 'table') return false;

  const rows = element.children.filter((c) => c.role === 'row');
  if (rows.length === 0 || rows.length > 2) return false;

  let totalCells = 0;
  let interactiveCells = 0;
  for (const row of rows) {
    for (const cell of row.children) {
      if (cell.role === 'cell' || cell.role === 'gridcell' || cell.role === 'columnheader') {
        totalCells++;
        if (hasInteractiveDescendants(cell)) {
          interactiveCells++;
        }
      }
    }
  }

  return totalCells > 0 && interactiveCells >= totalCells * 0.5;
}

/**
 * Compute selection priority for an interactive element inside a grid cell.
 */
function gridCellPriority(el: {
  role: string;
  isClickableGeneric: boolean;
  isActive: boolean;
}): number {
  if (el.isActive) return 5;
  if (FORM_INPUT_ROLES.has(el.role)) return 4;
  if (el.isClickableGeneric) return 3;
  if (el.role !== 'button') return 2;
  return 1;
}

/**
 * The current display value of a select-like button (e.g. react-aria
 * `<Select>`), which lives in child generic node(s) (the SelectValue span),
 * not in the button's own accessible name. Returns the joined child text, or
 * '' if the button has no value-carrying generic children (e.g. an icon-only
 * button), so callers can fall back to their existing logic.
 */
function selectValueFromChildren(el: SnapshotElement): string {
  const parts: string[] = [];
  for (const child of el.children) {
    if (child.role === 'generic') {
      const text = child.text || child.inputValue;
      if (text) parts.push(text.trim());
    }
  }
  return stripBlankPatterns(parts.join(' ')).trim();
}

/**
 * Recover a select-like control's display value (and the ref of the button
 * that shows it) from anywhere in a cell subtree. react-aria renders a hidden
 * native `<combobox>` for form submission that outranks the visible select
 * button in cell-priority but carries no inline value — so the cell would
 * otherwise render blank. Walk the subtree for the first button whose generic
 * children carry a real value (the SelectValue) and surface it.
 */
function recoverSelectValue(cellEl: SnapshotElement): { ref: string; value: string } | null {
  let result: { ref: string; value: string } | null = null;
  const walk = (node: SnapshotElement): void => {
    if (result) return;
    if (node.role === 'button' && node.ref) {
      const v = selectValueFromChildren(node);
      if (v && !isNoisyLabel(v)) {
        result = { ref: node.ref, value: v };
        return;
      }
    }
    for (const child of node.children) walk(child);
  };
  walk(cellEl);
  return result;
}

function collectGridCellActions(cellEl: SnapshotElement): Array<{ ref: string; label: string }> {
  const actions: Array<{ ref: string; label: string }> = [];

  const walk = (node: SnapshotElement): void => {
    if (INTERACTIVE_LEAF_ROLES.has(node.role) && node.ref) {
      const label = stripBlankPatterns(
        node.text || node.attributes['aria-label'] || node.inputValue || ''
      ).trim();
      // Select-like buttons carry their value in a child node, not node.text;
      // prefer that over "(unlabeled icon)" so the action reads e.g. "Member".
      const childLabel = selectValueFromChildren(node);
      const fallbackLabel = hasDescendantRole(node, 'img')
        ? '(unlabeled icon; see HINTS)'
        : '(icon)';
      const usableLabel =
        label && !isNoisyLabel(label)
          ? label
          : childLabel && !isNoisyLabel(childLabel)
            ? childLabel
            : fallbackLabel;
      actions.push({
        ref: node.ref,
        label: usableLabel,
      });
      return;
    }
    for (const child of node.children) walk(child);
  };

  walk(cellEl);
  return actions;
}

function hasDescendantRole(node: SnapshotElement, role: string): boolean {
  for (const child of node.children) {
    if (child.role === role || hasDescendantRole(child, role)) return true;
  }
  return false;
}

function rowLabel(rowEl: SnapshotElement): string | undefined {
  const raw = rowEl.attributes['aria-label'] || rowEl.text || '';
  const label = stripBlankPatterns(raw).replace(/\s+/g, ' ').trim();
  if (!label) return undefined;
  if (label.length > 140) return undefined;
  if (label === rowEl.role) return undefined;
  return label;
}

/**
 * Extract text content from a non-interactive gridcell.
 */
export function extractCellText(el: SnapshotElement): string | null {
  const collectLeafText = (node: SnapshotElement, out: string[]): void => {
    if (INTERACTIVE_LEAF_ROLES.has(node.role)) return;
    const direct = node.inputValue || node.text;
    if (direct) out.push(direct.trim());
    for (const child of node.children) collectLeafText(child, out);
  };

  const findFirstParagraph = (node: SnapshotElement): string | null => {
    if (node.role === 'paragraph' && node.text) {
      return node.text.trim();
    }
    for (const child of node.children) {
      if (child.role === 'generic' || child.role === 'paragraph') {
        const found = findFirstParagraph(child);
        if (found) return found;
      }
    }
    return null;
  };

  const paragraphText = findFirstParagraph(el);
  if (paragraphText) return paragraphText;

  const text = el.text?.trim();
  if (text) return text;

  const leafText: string[] = [];
  for (const child of el.children) collectLeafText(child, leafText);
  const joined = leafText
    .map((part) => stripBlankPatterns(part).trim())
    .filter((part) => part && !isNoisyLabel(part))
    .join(' ')
    .trim();
  if (joined) return joined;

  return null;
}

/**
 * Extract cell data from a gridcell element.
 */
export function extractGridCell(cellEl: SnapshotElement): GridCell | null {
  const interactiveElements: Array<{
    ref: string;
    column: string;
    value: string;
    role: string;
    isClickableGeneric: boolean;
    isActive: boolean;
  }> = [];

  const findInteractive = (el: SnapshotElement): void => {
    if (isClickableByAttribute(el) && el.ref && el.role === 'generic') {
      const childTexts: string[] = [];
      for (const child of el.children) {
        if (child.role === 'generic') {
          const childText = child.text || child.inputValue;
          if (childText) childTexts.push(childText);
        }
      }
      interactiveElements.push({
        ref: el.ref,
        column: el.attributes['label'] || childTexts[0] || '',
        value: childTexts.join(' - ') || el.text || el.inputValue || '',
        role: el.role,
        isClickableGeneric: true,
        isActive: isActiveElement(el),
      });
    }

    if (INTERACTIVE_LEAF_ROLES.has(el.role) && el.ref) {
      // Stateful toggles need three things visible in the cell:
      //   - the role (so the agent knows it's a togglable control, not text)
      //   - the on/off state (so the agent knows the current value)
      //   - the toggle's own accessible name (so the agent can confirm which
      //     row it refers to — redundant with the row label, but reassuring).
      // Format: `switch "Personal Organization" (off)`
      let value: string;
      if (el.role === 'checkbox' || el.role === 'switch') {
        const on = el.attributes['checked'] === 'true';
        const state = el.role === 'switch' ? (on ? 'on' : 'off') : on ? 'checked' : 'unchecked';
        const ownLabel = (el.text || el.inputValue || '').trim();
        value = ownLabel ? `${el.role} "${ownLabel}" (${state})` : `${el.role} (${state})`;
      } else {
        // Select-like buttons (e.g. react-aria <Select>) hold their current
        // value in a child node (<span class="react-aria-SelectValue">Member</span>).
        // The button's own accessible name composes the value with the field
        // label ("Member Role for George Mayer"), which reads as noise and gets
        // dropped from the row. Prefer the child value text so the cell renders
        // `Role: Member`. Mirrors the clickable-generic branch above.
        value =
          selectValueFromChildren(el) ||
          el.inputValue ||
          el.text ||
          el.attributes['aria-label'] ||
          '';
      }
      interactiveElements.push({
        ref: el.ref,
        column: el.attributes['label'] || el.text || el.attributes['aria-label'] || '',
        value,
        role: el.role,
        isClickableGeneric: false,
        isActive: isActiveElement(el),
      });
    }

    for (const child of el.children) findInteractive(child);
  };

  findInteractive(cellEl);

  if (interactiveElements.length === 0) {
    const textValue = extractCellText(cellEl);
    if (textValue && !isNoisyLabel(textValue)) {
      return {
        ref: cellEl.ref,
        column: cellEl.text || '',
        value: textValue,
        hasValue: true,
        dataField: cellEl.dataField,
        isEditable: cellEl.isEditable,
      };
    }
    // Empty but editable cell (e.g. a MUI DataGrid editable rate cell with no
    // current value). Surface it — carrying its `data-field` for the locator
    // and a column name resolved later from `columnByPosition` — so the agent
    // can target it by ref instead of coordinate-clicking. Bounded to
    // editable cells so the snapshot doesn't bloat with every empty cell.
    if (cellEl.isEditable && cellEl.ref) {
      return {
        ref: cellEl.ref,
        column: '',
        value: '',
        hasValue: false,
        dataField: cellEl.dataField,
        isEditable: true,
      };
    }
    return null;
  }

  const found = interactiveElements.reduce((best, current) =>
    gridCellPriority(current) > gridCellPriority(best) ? current : best
  );

  const column = stripBlankPatterns(found.column);
  const rawValue = isBlankValue(found.value) ? '' : stripBlankPatterns(found.value).trim();
  let value = isNoisyLabel(rawValue) ? '' : rawValue;
  let ref = found.ref;
  let role = found.role;
  const gridcellLabel = cellEl.text ? stripBlankPatterns(cellEl.text).trim() : undefined;

  // react-aria <Select> renders a hidden form combobox alongside the visible
  // select button. That combobox outranks the button in cell-priority but its
  // value is unreliable — either blank or the concatenation of every option
  // ("AdminMember"). The button's child SelectValue is the authoritative
  // current value, so prefer it whenever the winner is a combobox (or produced
  // no value at all). Leaves genuine standalone comboboxes untouched: recovery
  // only fires when a value-bearing select button exists in the cell.
  if (!value || found.role === 'combobox') {
    const recovered = recoverSelectValue(cellEl);
    if (recovered) {
      value = recovered.value;
      ref = recovered.ref;
      role = 'button';
    }
  }

  return {
    ref,
    column: column.trim(),
    value,
    hasValue: value !== '',
    gridcellLabel: gridcellLabel || undefined,
    role,
    dataField: cellEl.dataField,
    isEditable: cellEl.isEditable,
  };
}

/**
 * Reconcile a single cell's column name against known columns.
 */
export function reconcileCellColumn(cell: GridCell, knownColumns: Set<string>): GridCell {
  if (knownColumns.has(cell.column)) return cell;

  if (cell.gridcellLabel && knownColumns.has(cell.gridcellLabel)) {
    const newValue = cell.column || cell.value;
    const cleanValue = isBlankValue(newValue) ? '' : newValue.trim();
    return {
      ...cell,
      column: cell.gridcellLabel,
      value: cleanValue,
      hasValue: cleanValue !== '',
    };
  }

  let bestMatch = '';
  for (const col of knownColumns) {
    if (
      cell.column.startsWith(col) &&
      col.length > bestMatch.length &&
      (cell.column.length === col.length || cell.column[col.length] === ' ')
    ) {
      bestMatch = col;
    }
  }

  if (bestMatch) {
    const remainder = cell.column.slice(bestMatch.length).trim();
    const newValue = isBlankValue(remainder) ? '' : remainder;
    return {
      ...cell,
      column: bestMatch,
      value: newValue || cell.value,
      hasValue: (newValue || cell.value) !== '',
    };
  }

  return cell;
}

/**
 * Reconcile all grid cells against known column names.
 */
function reconcileGridCells(grid: GridInfo): GridInfo {
  if (grid.columns.length === 0) return grid;

  const knownColumns = new Set(grid.columns.map((c) => c.name));

  return {
    ...grid,
    rows: grid.rows.map((row) => {
      const reconciledCells = row.cells.map((cell) => {
        const reconciled = reconcileCellColumn(cell, knownColumns);
        if (knownColumns.has(reconciled.column)) return reconciled;

        if (
          grid.columnByPosition &&
          cell.positionInRow !== undefined &&
          grid.headerChildCount !== undefined &&
          row.totalChildren === grid.headerChildCount
        ) {
          const positionalColumn = grid.columnByPosition.get(cell.positionInRow);
          if (positionalColumn) {
            // Prefer cell.value when it's distinct from cell.column —
            // stateful toggles encode their on/off into value (e.g.
            // "Foo — on") and dropping it to column loses the state.
            const preferValue =
              !isBlankValue(cell.value) && cell.value.trim() !== cell.column.trim();
            const newValue = preferValue ? cell.value : cell.column || cell.value;
            const cleanValue = isBlankValue(newValue) ? '' : newValue.trim();
            return {
              ...cell,
              column: positionalColumn,
              value: cleanValue,
              hasValue: cleanValue !== '',
            };
          }
        }

        return reconciled;
      });

      if (grid.headerCellPositions && row.cellPositions) {
        const claimedColumns = new Set(
          reconciledCells.filter((c) => knownColumns.has(c.column)).map((c) => c.column)
        );

        for (let idx = 0; idx < reconciledCells.length; idx++) {
          const cell = reconciledCells[idx];
          if (knownColumns.has(cell.column) || cell.positionInRow === undefined) continue;

          const cellOrdinal = row.cellPositions.indexOf(cell.positionInRow);
          if (cellOrdinal >= 0 && cellOrdinal < grid.headerCellPositions.length) {
            const headerPos = grid.headerCellPositions[cellOrdinal];
            const positionalColumn = grid.columnByPosition?.get(headerPos);
            if (positionalColumn && !claimedColumns.has(positionalColumn)) {
              const preferValue =
                !isBlankValue(cell.value) && cell.value.trim() !== cell.column.trim();
              const newValue = preferValue ? cell.value : cell.column || cell.value;
              const cleanValue = isBlankValue(newValue) ? '' : newValue.trim();
              reconciledCells[idx] = {
                ...cell,
                column: positionalColumn,
                value: cleanValue,
                hasValue: cleanValue !== '',
              };
              claimedColumns.add(positionalColumn);
            }
          }
        }
      }

      return { ...row, cells: reconciledCells };
    }),
  };
}

/**
 * Detect if an element is a grid/treegrid and extract its structure.
 */
export function detectGrid(element: SnapshotElement): GridInfo | null {
  if (element.role !== 'grid' && element.role !== 'treegrid' && element.role !== 'table') {
    return null;
  }
  const isHTMLTable = element.role === 'table';

  const columns: GridColumn[] = [];
  const rows: GridRow[] = [];
  let headerRowRef: string | null = null;
  const columnByPosition = new Map<number, string>();
  let headerChildCount: number | undefined;
  let headerCellPositions: number[] | undefined;

  /**
   * Check if an element is structurally row-like even if its role isn't 'row'.
   * Some apps override the row role (e.g., MUI DataGrid with row selection uses 'checkbox').
   * We treat an element as a row if it has 2+ cell/gridcell children.
   */
  const isRowLike = (el: SnapshotElement): boolean => {
    if (el.role === 'row') return true;
    const cellCount = el.children.filter((c) => c.role === 'cell' || c.role === 'gridcell').length;
    return cellCount >= 2;
  };

  const processRow = (rowEl: SnapshotElement, index: number): GridRow | null => {
    if (!isRowLike(rowEl)) return null;

    const hasColumnHeaders = rowEl.children.some((c) => c.role === 'columnheader');
    if (hasColumnHeaders) {
      headerRowRef = rowEl.ref;
      const columnHeaders = rowEl.children.filter((c) => c.role === 'columnheader');
      const childful = columnHeaders.filter((c) => c.children.length > 0);
      const textBearingChildless = columnHeaders.filter((c) => c.children.length === 0 && c.text);
      // Skip childless columnheaders only when they're a minority relative to childful ones
      // (likely chrome like icon/kebab columns). When most text columns are childless, they're
      // real data columns (e.g., MUI DataGrid with simple text headers).
      const skipChildlessHeaders =
        childful.length > 0 && textBearingChildless.length <= childful.length;

      const columnsBefore = columns.length;
      for (let i = 0; i < rowEl.children.length; i++) {
        const child = rowEl.children[i];
        if (child.role === 'columnheader') {
          if (skipChildlessHeaders && child.children.length === 0) continue;

          const primaryLabel = findPrimaryColumnLabel(child);
          const rawName =
            (primaryLabel ? getElementText(primaryLabel) : undefined) || child.text || '';
          const cleanName = rawName.replace(/,?\s*sorted in \w+ order/i, '').trim();
          if (cleanName) {
            columnByPosition.set(i, cleanName);
            columns.push({ name: cleanName, ref: primaryLabel?.ref || child.ref });
          } else {
            const checkboxChild = findDescendantByRole(child, 'checkbox');
            if (checkboxChild?.ref) {
              const syntheticName = '☐';
              columnByPosition.set(i, syntheticName);
              columns.push({ name: syntheticName, ref: checkboxChild.ref });
            }
          }
        }
      }
      if (columns.length > columnsBefore) {
        headerChildCount = rowEl.children.length;
        headerCellPositions = Array.from(columnByPosition.keys()).sort((a, b) => a - b);
      }
      return null;
    }

    if (isHTMLTable && columns.length === 0) {
      const cellChildren = rowEl.children.filter((c) => c.role === 'cell');
      const textBearingCells = cellChildren.filter((c) => {
        if (c.text) return true;
        return c.children.some((ch) => ch.text || ch.inputValue);
      });

      if (cellChildren.length > 0 && textBearingCells.length >= cellChildren.length * 0.5) {
        headerRowRef = rowEl.ref;
        const columnsBefore = columns.length;
        for (let i = 0; i < rowEl.children.length; i++) {
          const child = rowEl.children[i];
          if (child.role !== 'cell') continue;

          let colName = child.text || '';
          if (!colName) {
            for (const ch of child.children) {
              const childText = ch.text || ch.inputValue;
              if (childText) {
                colName = childText;
                break;
              }
            }
          }
          const cleanName = colName.replace(/,?\s*sorted in \w+ order/i, '').trim();
          if (cleanName) {
            columnByPosition.set(i, cleanName);
            const childWithRef = child.children.find((ch) => ch.ref && (ch.text || ch.inputValue));
            columns.push({ name: cleanName, ref: childWithRef?.ref || child.ref });
          } else {
            const checkboxChild = findDescendantByRole(child, 'checkbox');
            if (checkboxChild?.ref) {
              const syntheticName = '☐';
              columnByPosition.set(i, syntheticName);
              columns.push({ name: syntheticName, ref: checkboxChild.ref });
            }
          }
        }
        if (columns.length > columnsBefore) {
          headerChildCount = rowEl.children.length;
          headerCellPositions = Array.from(columnByPosition.keys()).sort((a, b) => a - b);
        }
        return null;
      }
    }

    const cells: GridCell[] = [];
    const rowActions: Array<{ ref: string; label: string }> = [];
    const isSelected = rowEl.attributes['selected'] === 'true' || subtreeContainsActive(rowEl);

    for (let i = 0; i < rowEl.children.length; i++) {
      const cellEl = rowEl.children[i];
      if (cellEl.role === 'gridcell' || cellEl.role === 'cell' || cellEl.role === 'rowheader') {
        const rowHeaderText = cellEl.role === 'rowheader' ? extractCellText(cellEl) : undefined;
        const cellData = extractGridCell(cellEl);
        if (cellData && (cellEl.role !== 'rowheader' || rowHeaderText)) {
          cellData.positionInRow = i;
          cells.push(cellData);
          if (!columnByPosition.has(i)) {
            rowActions.push(...collectGridCellActions(cellEl));
          }
        }
        if (cellEl.role !== 'rowheader') continue;

        const findButtons = (node: SnapshotElement): void => {
          if (node.role === 'button' && node.ref) {
            rowActions.push({ ref: node.ref, label: node.text || 'expand' });
          }
          for (const child of node.children) findButtons(child);
        };
        findButtons(cellEl);
      }
    }

    const cellPositions: number[] = [];
    for (let i = 0; i < rowEl.children.length; i++) {
      if (
        rowEl.children[i].role === 'gridcell' ||
        rowEl.children[i].role === 'cell' ||
        rowEl.children[i].role === 'rowheader'
      ) {
        cellPositions.push(i);
      }
    }

    const row: GridRow = {
      ref: rowEl.ref,
      index,
      label: rowLabel(rowEl),
      isSelected,
      cells,
      totalChildren: rowEl.children.length,
      cellPositions,
    };

    if (cells.length === 0 && rowEl.children.some((c) => c.role === 'rowheader')) {
      const labels: string[] = [];
      const buttons: Array<{ ref: string; label: string }> = [];

      for (const child of rowEl.children) {
        if (child.role === 'rowheader') {
          const collectContent = (node: SnapshotElement): void => {
            if (node.role === 'button' && node.ref) {
              buttons.push({ ref: node.ref, label: node.text || '' });
              return;
            }
            if (
              node.role === 'generic' &&
              node.text &&
              node.children.every((c) => c.role === 'img' || !c.text)
            ) {
              labels.push(node.text);
            }
            for (const c of node.children) collectContent(c);
          };
          collectContent(child);
        }
      }

      if (labels.length > 0 || buttons.length > 0) {
        row.expandedContent = { labels, buttons };
        rowActions.length = 0;
      }
    }

    if (rowActions.length > 0) row.rowActions = rowActions;
    return row;
  };

  const findRows = (el: SnapshotElement): void => {
    if (isRowLike(el)) {
      const row = processRow(el, rows.length);
      if (row) rows.push(row);
    } else {
      for (const child of el.children) findRows(child);
    }
  };

  for (const child of element.children) {
    findRows(child);
  }

  const rawGrid: GridInfo = {
    ref: element.ref,
    columns,
    rows,
    headerRowRef,
    totalRows: rows.length,
    containsActive: subtreeContainsActive(element),
    columnByPosition: columnByPosition.size > 0 ? columnByPosition : undefined,
    headerChildCount,
    headerCellPositions,
  };

  return reconcileGridCells(rawGrid);
}

/**
 * Return true when a ref belongs to a rendered item inside the grid.
 */
export function gridContainsRef(grid: GridInfo, targetRef: string): boolean {
  for (const column of grid.columns) {
    if (column.ref === targetRef) return true;
  }

  for (const row of grid.rows) {
    if (row.ref === targetRef) return true;
    if (row.cells.some((cell) => cell.ref === targetRef)) return true;
    if (row.rowActions?.some((action) => action.ref === targetRef)) return true;
    if (row.expandedContent?.buttons.some((button) => button.ref === targetRef)) return true;
  }

  return false;
}

/**
 * Count the rendered semantic units surfaced by a grid section.
 */
export function countRenderedGridItems(grid: GridInfo): number {
  const knownColumns =
    grid.columns.length > 0 ? new Set(grid.columns.map((c) => c.name)) : undefined;

  let count = 0;
  for (const row of grid.rows) {
    count += row.cells.filter(
      (c) => c.hasValue && (!knownColumns || knownColumns.has(c.column))
    ).length;
    count += row.rowActions?.length ?? 0;
    count += row.expandedContent?.buttons.length ?? 0;
  }

  return count;
}

/**
 * Format grid output in compact form.
 * When `expanded` is true, all rows are shown without the MAX_GRID_ROWS limit.
 */
export function formatGridOutput(grid: GridInfo, indent: number, expanded = false): string {
  const indentStr = '  '.repeat(indent);
  const lines: string[] = [];

  const colInfo = grid.columns.length > 0 ? `${grid.columns.length} columns` : 'unknown columns';
  lines.push(`${indentStr}GRID [${grid.ref}]: ${grid.totalRows} rows, ${colInfo}`);

  const MAX_DISPLAY_COLUMNS = 10;
  if (grid.columns.length > 0) {
    const displayColumns = grid.columns.slice(0, MAX_DISPLAY_COLUMNS);
    const colsWithRefs = displayColumns.map((c) => `${c.name} [${c.ref}]`).join(', ');
    const moreColsNote =
      grid.columns.length > MAX_DISPLAY_COLUMNS
        ? ` (+${grid.columns.length - MAX_DISPLAY_COLUMNS} more)`
        : '';
    lines.push(`${indentStr}  Columns: ${colsWithRefs}${moreColsNote}`);
  }
  lines.push('');

  const knownColumns =
    grid.columns.length > 0 ? new Set(grid.columns.map((c) => c.name)) : undefined;

  const MAX_GRID_ROWS = 30;
  const displayRows = expanded ? grid.rows : grid.rows.slice(0, MAX_GRID_ROWS);
  for (const row of displayRows) {
    lines.push(formatGridRow(row, indent + 1, knownColumns, expanded));
  }
  if (!expanded && grid.rows.length > MAX_GRID_ROWS) {
    lines.push(
      `${indentStr}  ... and ${grid.rows.length - MAX_GRID_ROWS} more rows (use expand="${grid.ref}" to see all)`
    );
  }

  return lines.join('\n');
}

/**
 * Format a single grid row.
 */
/**
 * Max empty editable cells surfaced per row in the COMPACT grid view. Empty
 * editable cells are valuable (they're targetable fill points the agent would
 * otherwise only reach by coordinate-clicking) but a fully-editable sparse grid
 * has one per column per row — rendering them all blows past the grid size
 * budget and collapses the whole grid. Cap in compact mode with a visible
 * "+N more editable" note; the expanded view (`expand=<ref>`) lists every one.
 */
const MAX_COMPACT_EDITABLE_CELLS_PER_ROW = 4;

export function formatGridRow(
  row: GridRow,
  indent: number,
  knownColumns?: Set<string>,
  expanded = false
): string {
  const indentStr = '  '.repeat(indent);

  if (row.expandedContent) {
    const parts: string[] = [];
    if (row.expandedContent.labels.length > 0) {
      parts.push(row.expandedContent.labels.join(', '));
    }
    for (const btn of row.expandedContent.buttons) {
      parts.push(`${btn.label} [${btn.ref}]`);
    }
    return `${indentStr}  ↳ Expanded [${row.ref}]: ${parts.join(' | ')}`;
  }

  const marker = row.isSelected ? 'ACTIVE → Row' : 'Row';
  const label = row.label ? ` "${row.label}"` : '';
  const actionsPrefix = row.rowActions?.length
    ? row.rowActions.map((a) => `[action ${a.ref}: ${a.label}]`).join(' ') + ' '
    : '';

  const inScope = (c: GridCell) => !knownColumns || knownColumns.has(c.column);
  const valueCells = row.cells.filter((c) => c.hasValue && inScope(c));
  const editableEmptyCells = row.cells.filter((c) => !c.hasValue && c.isEditable && inScope(c));

  if (valueCells.length > 0 || editableEmptyCells.length > 0) {
    const renderCell = (c: GridCell): string => {
      // Empty editable cell: render the column + ref with an `(editable)`
      // marker so the agent knows it's a targetable, fillable cell.
      if (!c.hasValue && c.isEditable) {
        return `${c.column} [${c.ref}]: (editable)`;
      }
      if (c.column === c.value) {
        return `${c.column} [${c.ref}]`;
      }
      return `${c.column} [${c.ref}]: ${c.value}`;
    };

    const cellStrs = valueCells.map(renderCell);

    // Surface empty editable cells — all of them when expanded, a capped
    // prefix in the compact view so a fully-editable grid doesn't blow the
    // size budget. The overflow note keeps the truncation explicit.
    const shownEditable = expanded
      ? editableEmptyCells
      : editableEmptyCells.slice(0, MAX_COMPACT_EDITABLE_CELLS_PER_ROW);
    cellStrs.push(...shownEditable.map(renderCell));
    const hiddenEditable = editableEmptyCells.length - shownEditable.length;
    if (hiddenEditable > 0) {
      cellStrs.push(`(+${hiddenEditable} more editable cells)`);
    }

    if (cellStrs.length > 0) {
      return `${indentStr}${actionsPrefix}${marker} [${row.ref}]${label}: ${cellStrs.join(' | ')}`;
    }
  }

  return `${indentStr}${actionsPrefix}${marker} [${row.ref}]${label}: (empty row)`;
}
